// ResumeProjectsSection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

import { SortableSection } from "../components/SortableSection";
import { SortableCredit } from "../components/SortableCredit";

const ResumeProjectsSection = ({
  groupedResumeProjects = [],
  editMode,
  setResumeDialogOpen,
  setSelectedSectionTitle,
  setSelectedSectionId,
  setEditInitialData,
  setEditSection,
  setEditPromptOpen,
  setSectionToDelete,
  setDeleteSectionDialogOpen,
  setProjectToDelete,
  setDeleteProjectDialogOpen
}) => {
  const [sections, setSections] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);

  /* --------------------------------------------------
     Build sections + credits
  -------------------------------------------------- */
  useEffect(() => {
    if (!Array.isArray(groupedResumeProjects)) {
      setSections([]);
      return;
    }

    // Prevent accidental wipe on empty payload
    if (groupedResumeProjects.length === 0 && sections.length > 0) {
      return;
    }

    const sectionMap = new Map();

    groupedResumeProjects.forEach(item => {
      const heading =
        item.sectionHeading ||
        item.resumeSectionName ||
        "Untitled Section";

      const sectionId = item.resumeSectionId;

      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          sectionHeading: heading,
          resumeSectionId: sectionId,
          sortOrder: item.sectionSortOrder || 0,
          credits: []
        });
      }

      if (
        item.resumeSectionDetailId !== null &&
        item.resumeSectionDetailId !== undefined
      ) {
        sectionMap.get(sectionId).credits.push({
          resumeSectionDetailId: item.resumeSectionDetailId,
          title: item.title,
          role: item.role,
          company: item.company,
          url: item.url,
          userId: item.userId,
          userBy: item.userBy,
          sortOrder: item.detailSortOrder || 0,
          resumeSectionId: item.resumeSectionId
        });
      }
    });

    const sortedSections = Array.from(sectionMap.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    sortedSections.forEach(section => {
      section.credits.sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
      );
    });

    setSections(sortedSections);
  }, [groupedResumeProjects, sections.length]);

  /* --------------------------------------------------
     Drag sensors
  -------------------------------------------------- */
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  /* --------------------------------------------------
     Derived state
  -------------------------------------------------- */
  const totalCreditsCount = sections.reduce(
    (sum, section) => sum + section.credits.length,
    0
  );

  const canReorder = totalCreditsCount > 1;
  const showActions = editMode && !reorderMode;
  const hasVisibleSections = sections.some(
    section => section.credits.length > 0
  );

  if (!hasVisibleSections && !editMode) {
    return null;
  }

  /* --------------------------------------------------
     Handlers
  -------------------------------------------------- */
  const handleEditProject = item => {
    setEditInitialData({
      resumeSectionDetailId: item.resumeSectionDetailId,
      resumeSectionId: item.resumeSectionId,
      title: item.title,
      role: item.role,
      company: item.company,
      url: item.url
    });
    setEditSection("Credits");
    setEditPromptOpen(true);
  };

  const handleDeleteProject = item => {
    setProjectToDelete(item);
    setDeleteProjectDialogOpen(true);
  };

  const handleAddRole = (heading, sectionId) => {
    setSelectedSectionTitle(heading);
    setSelectedSectionId(sectionId);
    setResumeDialogOpen(true);
    setEditInitialData({ sectionHeading: heading });
  };

  const handleDeleteSection = (heading, sectionId) => {
    setSectionToDelete({
      heading,
      resumeSectionId: sectionId
    });
    setDeleteSectionDialogOpen(true);
  };

  const onSectionDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(
      s => s.sectionHeading === active.id
    );
    const newIndex = sections.findIndex(
      s => s.sectionHeading === over.id
    );

    const newSections = arrayMove(
      sections,
      oldIndex,
      newIndex
    ).map((s, index) => ({
      ...s,
      sortOrder: index + 1
    }));

    setSections(newSections);
  };

  const onCreditDragEnd = sectionHeading => event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSections(prev =>
      prev.map(section => {
        if (section.sectionHeading !== sectionHeading)
          return section;

        const oldIndex = section.credits.findIndex(
          c => c.resumeSectionDetailId === active.id
        );
        const newIndex = section.credits.findIndex(
          c => c.resumeSectionDetailId === over.id
        );

        const newCredits = arrayMove(
          section.credits,
          oldIndex,
          newIndex
        ).map((c, index) => ({
          ...c,
          detailSortOrder: index + 1
        }));

        return { ...section, credits: newCredits };
      })
    );
  };

  /* --------------------------------------------------
     Render
  -------------------------------------------------- */
  return (
    <Box
      sx={{ backgroundColor: "#fff", p: 2, mt: 2 }}
      className="shadow margin0"
    >
      {/* Header */}
      <Box
        display="flex"
        sx={{
          fontWeight: "bold",
          color: "#202020",
          pb: 1,
          ml: 1,
          fontSize: "1rem",
          borderBottom: "1px solid #dedede"
        }}
      >
        <Box flex={1}>Project</Box>
        <Box flex={1}>Role</Box>
        <Box flex={1}>Director/Prod. Co.</Box>
      </Box>

      {/* ---------------- REORDER MODE ---------------- */}
      {reorderMode ? (
        <>
          {/* Instructions */}
          <Box
            sx={{
              backgroundColor: "#f9fafb",
              border: "1px dashed #c7c7c7",
              borderRadius: 1,
              p: 1.5,
              mb: 2,
              color: "#555",
              fontSize: "13px"
            }}
          >
            Drag sections or individual roles and drop them into the
            position you like.
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onSectionDragEnd}
          >
            <SortableContext
              items={sections.map(s => s.sectionHeading)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map(section => (
                <SortableSection
                  key={section.sectionHeading}
                  id={section.sectionHeading}
                >
                  <Box mb={3}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      {section.sectionHeading}
                    </Typography>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={onCreditDragEnd(
                        section.sectionHeading
                      )}
                    >
                      <SortableContext
                        items={
                          section.credits.length > 0
                            ? section.credits.map(
                                c => c.resumeSectionDetailId
                              )
                            : [`empty-${section.resumeSectionId}`]
                        }
                        strategy={verticalListSortingStrategy}
                      >
                        {section.credits.map(item => (
                          <SortableCredit
                            key={item.resumeSectionDetailId}
                            id={item.resumeSectionDetailId}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{ py: 1, ml: 1 }}
                            >
                              <Box flex={1}>{item.title}</Box>
                              <Box flex={1}>{item.role}</Box>
                              <Box flex={1}>{item.company}</Box>
                            </Box>
                          </SortableCredit>
                        ))}
                      </SortableContext>
                    </DndContext>
                  </Box>
                </SortableSection>
              ))}
            </SortableContext>
          </DndContext>
        </>
      ) : (
        /* ---------------- NORMAL MODE ---------------- */
        sections.map(section => (
          <Box key={section.sectionHeading} mb={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mx={1}
              my={2}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "capitalize"
                }}
              >
                {section.sectionHeading}
              </Typography>

              {showActions && (
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      handleAddRole(
                        section.sectionHeading,
                        section.resumeSectionId
                      )
                    }
                  >
                    Add Role
                  </Button>
                  <IconButton
                    onClick={() =>
                      handleDeleteSection(
                        section.sectionHeading,
                        section.resumeSectionId
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {section.credits.map(item => (
              <Box
                key={item.resumeSectionDetailId}
                display="flex"
                alignItems="center"
                sx={{ py: 1, ml: 1 }}
              >
                <Box flex={1}>{item.title}</Box>
                <Box flex={1}>{item.role}</Box>
                <Box flex={1}>{item.company}</Box>

                {showActions && (
                  <Box display="flex">
                    <IconButton
                      onClick={() => handleEditProject(item)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteProject(item)}
                    >
                      <DeleteIcon
                        fontSize="small"
                        color="error"
                      />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ))
      )}

      {/* Footer Actions */}
      {editMode && (
        <Box display="flex" gap={1} mt={2} ml={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditSection("AddProjectSection")}
          >
            Add New Credits
          </Button>

          {canReorder && (
            <Button
              size="small"
              variant={reorderMode ? "contained" : "outlined"}
              color="secondary"
              onClick={() => setReorderMode(!reorderMode)}
            >
              {reorderMode ? "Done Reordering" : "Reorder"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ResumeProjectsSection;
